/**
 * Hook for exporting performance data as CSV.
 * Generates a CSV file with date, portfolioValue, dailyPnl, cumulativeReturn columns
 * and triggers a browser download.
 */

import { useState, useCallback } from 'react';
import type { ExtendedTimeRange, PerformanceDataPoint } from '@/types/performance';

interface UseExportPerformanceReturn {
  exportCSV: () => void;
  isExporting: boolean;
}

/**
 * Generate CSV content from performance data points.
 */
function generateCSV(data: PerformanceDataPoint[]): string {
  const header = 'date,portfolioValue,dailyPnl,cumulativeReturn';
  const rows = data.map(
    (point) =>
      `${point.date},${point.value},${point.pnl},${point.cumulativeReturn}`
  );
  return [header, ...rows].join('\n');
}

/**
 * Generate the export filename based on account ID and data date range.
 */
function generateFilename(accountId: string, data: PerformanceDataPoint[]): string {
  const startDate = data[0]!.date;
  const endDate = data[data.length - 1]!.date;
  return `${accountId}_performance_${startDate}_to_${endDate}.csv`;
}

/**
 * Trigger a browser download for the given content.
 */
function triggerDownload(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Hook that provides CSV export functionality for performance data.
 *
 * @param accountId - The account ID for the filename
 * @param _timeFrame - The selected time frame (used for context, not directly in export)
 * @param data - The performance data points to export
 * @returns exportCSV function and isExporting state
 */
export function useExportPerformance(
  accountId: string,
  _timeFrame: ExtendedTimeRange,
  data: PerformanceDataPoint[]
): UseExportPerformanceReturn {
  const [isExporting, setIsExporting] = useState(false);

  const exportCSV = useCallback(() => {
    if (data.length === 0) return;

    setIsExporting(true);

    try {
      const csv = generateCSV(data);
      const filename = generateFilename(accountId, data);
      triggerDownload(csv, filename);
    } finally {
      // Brief delay to show export feedback before resetting
      setTimeout(() => {
        setIsExporting(false);
      }, 500);
    }
  }, [accountId, data]);

  return { exportCSV, isExporting };
}
